"use client";

import { Button } from "../ui/button";
import { DragEvent, PropsWithChildren, useRef, useState } from "react";
import { Form } from "../ui/form";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useIntlForm } from "@erp/shared-libs/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Separator } from "../ui/separator";
import { cn, dayjs, downloadFile } from "@erp/shared-libs/utils";
import { Label } from "../ui/label";
import { Download, Plus } from "lucide-react";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader } from "../ui/card";
import { zodTypeSafeErrorMessage } from "@erp/shared-libs/i18n";
import { toast } from "sonner";
import { DynamicFormField, DynamicFormMessage } from "../forms/dynamic-form";
import { CheckableSubmitButton } from "../forms/checkable-submit-button";
import { ExcelImportType } from "@erp/shared-libs/variables";
import { BaseImportRequest, BaseImportResponse } from "@erp/shared-libs/common";
import { requiredNumberSchema } from "@erp/shared-libs/common";

interface BatchImportDialogProps extends PropsWithChildren {
  importType: ExcelImportType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onImport: (data: BaseImportRequest) => Promise<BaseImportResponse | undefined>;
}

export const BatchImportDialog: FC<BatchImportDialogProps> = ({
  children,
  open,
  onOpenChange,
  ...props
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* ================================================================
       * Dialog 內容
       * ================================================================ */}
      <DialogContent
        hideClose
        className="w-11/12 max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <ImportExportDialogContent {...props} />
      </DialogContent>
    </Dialog>
  );
};

const ImportExportDialogContent: FC<Omit<BatchImportDialogProps, "open">> = ({
  onImport,
  importType,
}) => {
  // ===============================================
  // Hooks
  // ===============================================
  const { t } = useTranslation(["common"]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [resultState, setResultState] = useState<BaseImportResponse | undefined>(undefined);

  // ===============================================
  // Form
  // ===============================================
  const form = useIntlForm<{ file: File; importType: ExcelImportType }>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(
      z.object({
        importType: requiredNumberSchema,
        file: z
          .file()
          // 必填
          .refine((file) => file && file.size > 0, zodTypeSafeErrorMessage("required"))
          // 必須是 Excel 檔案
          .refine((file) => {
            if (!file) return false;
            const mime = file.type;
            const isXlsx =
              mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const isXls = mime === "application/vnd.ms-excel";
            return isXlsx || isXls;
          }, zodTypeSafeErrorMessage("must_be_excel_file")),
      })
    ),
    values: {
      file: new File([], ""),
      importType: importType,
    },
  });

  // ===============================================
  // Functions
  // ===============================================
  /**
   * 處理表單提交
   */
  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      form.handleSubmit(async (data) => {
        const res = await onImport({ ...data });

        setResultState(res);
      })(event);
    },
    [form, onImport]
  );

  /**
   * 處理拖曳進入時的動作
   */
  const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.currentTarget.dataset.dragging = "true";
  };

  /**
   * 處理拖曳離開時的動作
   */
  const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
    e.currentTarget.dataset.dragging = "false";
  };

  /**
   * 處理檔案上傳，檢查
   */
  const handleProcessFile = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const file = files[0];

      if (!file) return;

      const mime = file.type;
      const isXlsx = mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const isXls = mime === "application/vnd.ms-excel";

      if (!isXlsx && !isXls) {
        toast.error(t("common:dialog.import_export.error.file_type"));
        return;
      }

      form.setValue("file", file, { shouldValidate: true, shouldDirty: true });
    },
    [form, t]
  );

  /**
   * 處理拖曳檔案上傳
   */
  const handleDrop = useCallback(
    (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.currentTarget.dataset.dragging = "false";

      // 處理檔案
      handleProcessFile(e.dataTransfer.files);

      // 要清空 input 的值，否則下一個檔案選擇會無法觸發 onChange
      fileInputRef.current!.value = "";
    },
    [handleProcessFile]
  );

  /**
   * 處理檔案選擇
   */
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleProcessFile(e.target.files);
    },
    [handleProcessFile]
  );

  /**
   * 下載失敗回傳文件
   */
  const handleDownloadFailureFile = useCallback(() => {
    if (!resultState?.blob) return;

    downloadFile(
      resultState.blob,
      resultState.fileName ?? `Errors_${dayjs().format("YYYYMMDDHHmm")}.xlsx`
    );
  }, [resultState]);

  const handleReImport = useCallback(() => {
    setResultState(undefined);
    form.setValue("file", undefined as unknown as File, { shouldValidate: true });
  }, [form]);

  // ===============================================
  // Render
  // ===============================================
  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex flex-col pt-4">
          <DialogHeader className="mb-2 px-4">
            <DialogTitle asChild>
              <div className="text-lg font-bold">
                {importType === ExcelImportType.InsertOnly
                  ? t("common:dialog.import_export.title.add")
                  : t("common:dialog.import_export.title.edit")}
              </div>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mb-4 text-sm">
              {t("common:dialog.import_export.message")}
            </DialogDescription>
            {/* ================================================================
             * 注意事項
             * ================================================================ */}
            <Card className="bg-grid-paper shadow-none">
              <CardContent className="space-y-2 p-4 text-sm">
                <p className="text-primary text-left font-bold">注意事項</p>
                <ul className="list-disc space-y-1 pl-4 text-left">
                  <li>請使用匯出的檔案格式進行匯入</li>
                  <li>請勿刪除或修改欄位名稱</li>
                  <li>請依照欄位說明填寫資料</li>
                </ul>
              </CardContent>
            </Card>
          </DialogHeader>
          {/* ================================================================
           * Tab 切換
           * ================================================================ */}
          <Separator />
          {/* =================================================
           * Tab 內容
           * ==================================================*/}
          <div className="bg-background flex flex-col p-4">
            {resultState ? (
              <>
                {/* ================================================================
                 * 匯入結果
                 * ================================================================ */}
                <Card className="bg-on-background shadow-none">
                  <CardHeader className="py-2 text-center">匯入結果</CardHeader>
                  <Separator />
                  <CardContent className="space-y-2 p-4 text-sm">
                    {resultState.isSuccess ? (
                      <div
                        className="border-primary bg-primary-50 text-primary animate-in fade-in-0 slide-in-from-bottom-2 space-y-1
                          rounded-md border p-2 duration-500"
                      >
                        <p>匯入成功</p>
                        <p className="text-xs">總匯入：{resultState.totalCount} 筆</p>
                        <p className="text-xs">新增：{resultState.insertedCount} 筆</p>
                        <p className="text-xs">更新：{resultState.updatedCount} 筆</p>
                      </div>
                    ) : (
                      <div
                        className="border-destructive bg-destructive/10 text-destructive animate-in fade-in-0 slide-in-from-bottom-2
                          flex items-center rounded-md border p-2 duration-500"
                      >
                        <div>
                          <p>匯入失敗</p>
                          <p className="text-xs">請下載失敗回傳文件，修改後重新上傳</p>
                        </div>
                        <Button
                          size="icon"
                          variant="link"
                          className="text-destructive ml-auto"
                          onClick={handleDownloadFailureFile}
                        >
                          <Download size={14} />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* ================================================================
                 * 檔案上傳
                 * ================================================================ */}
                <DynamicFormField
                  control={form.control}
                  name="file"
                  render={({}) => (
                    <Label
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      className={cn(
                        `border-input bg-on-background data-[dragging=true]:border-primary relative flex aspect-square h-40
                          shrink-0 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border
                          border-dashed text-sm data-[dragging=true]:border-2`
                      )}
                    >
                      {form.watch("file").name ? (
                        <div className="flex flex-col items-center p-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 256 256"
                            width={24}
                            height={24}
                            className="fill-input mb-2"
                          >
                            <path d="M200,24H72A16,16,0,0,0,56,40V64H40A16,16,0,0,0,24,80v96a16,16,0,0,0,16,16H56v24a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V40A16,16,0,0,0,200,24Zm-40,80h40v48H160Zm40-16H160V80a16,16,0,0,0-16-16V40h56ZM72,40h56V64H72ZM40,80H144v79.83c0,.06,0,.11,0,.17s0,.11,0,.17V176H40ZM72,192h56v24H72Zm72,24V192a16,16,0,0,0,16-16v-8h40v48ZM65.85,146.88,81.59,128,65.85,109.12a8,8,0,0,1,12.3-10.24L92,115.5l13.85-16.62a8,8,0,1,1,12.3,10.24L102.41,128l15.74,18.88a8,8,0,0,1-12.3,10.24L92,140.5,78.15,157.12a8,8,0,0,1-12.3-10.24Z"></path>
                          </svg>
                          <p className="max-w-xs overflow-hidden text-ellipsis">
                            {form.watch("file").name}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center p-4">
                          <Plus className="text-input mb-4" />
                          <p>可點擊選取或拖曳檔案至框線上傳</p>
                        </div>
                      )}
                      <DynamicFormMessage className="text-center" />
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="-z-1 absolute right-0 top-0 h-full w-full border opacity-0"
                        onChange={handleFileChange}
                        multiple
                      />
                    </Label>
                  )}
                />
              </>
            )}
          </div>
        </div>
        <Separator />
        <div className="flex items-end justify-end px-4 py-2">
          {/* ================================================================
           * 取消按鈕
           * ================================================================ */}
          <DialogClose asChild>
            <Button variant="outline" size="sm" className="px-8">
              {t("common:button.close")}
            </Button>
          </DialogClose>
          {/* ================================================================
           * 匯入按鈕
           * ================================================================ */}
          {resultState ? (
            <Button variant="dark" size="sm" className="ml-2 px-8" onClick={handleReImport}>
              {t("common:dialog.import_export.button.re-import")}
            </Button>
          ) : (
            // 只有選檔＋匯入型別，值由 values 灌入不算變更，不要求先有變更
            <CheckableSubmitButton
              targetForm={form}
              requireDirty={false}
              disabled={!form.formState.isValid}
            >
              {t("common:button.import")}
            </CheckableSubmitButton>
          )}
        </div>
      </form>
    </Form>
  );
};
